import styled from '@emotion/styled';
import { device } from '@landsbankinn/ui';
import React, { useRef, useEffect } from 'react';
import { animated, useSpring, config } from 'react-spring';
import { ScrollDownArrow } from '../../components/icons/ScrollDownArrow';
import { Subnav } from '../../components/subnav/Subnav';

interface IProps {
  title?: string;
  image: React.ReactNode;
  shadeOpacity: number;
}

const Host = styled(animated.div)`
  position: relative;
  z-index: 0;
  height: calc(100vh - 80px);

  overflow: hidden;

  ${Subnav.Host} + div &:nth-child(1) {
    height: calc(100vh - 80px - 52px);
  }
`;

const Content = styled.div<{ shadeOpacity?: number }>`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;

  background-image: linear-gradient(
    0deg,
    rgba(0, 0, 0, ${(props) => props.shadeOpacity || 0.6}) 0%,
    rgba(0, 0, 0, ${(props) => props.shadeOpacity || 0.1}) 50%,
    rgba(0, 0, 0, 0) 70%
  );
`;

const Text = styled(animated.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 100%;

  padding-bottom: 40px;
`;

const H1 = styled.h1`
  font-size: 30px;
  line-height: ${44 / 35};
  text-align: center;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.93px;
  font-style: normal;

  color: #fff;

  @media ${device.mobile} {
    font-size: 35px;
  }
`;

const Button = styled(animated.button)`
  margin-bottom: 10px;
  padding: 30px;

  cursor: pointer;

  @keyframes bounce-arrow {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(30px);
    }
    60% {
      transform: translateY(15px);
    }
  }

  > svg {
    transform: translateY(0);
    transition: transform 200ms ease-in-out;
    animation: bounce-arrow 2s ease-in-out;
    animation-delay: 0.5s;
  }

  &:hover > svg {
    transform: translateY(10px);
  }
`;

const Image = styled(animated.div)`
  position: fixed;
  z-index: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  height: 100vh;
  width: 100%;
  object-fit: cover;

  > div {
    height: 100%;
    transform: scale(1.05);
  }
`;

const SmoothWhiteArea = styled(animated.div)`
  position: absolute;
  bottom: -150px;
  left: 0;
  right: 0;
  height: 150px;
  background: ${(props) => props.theme.colors.background};
`;

export const interpolate = (
  x: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number => {
  if (x1 === x2) {
    return (y1 + y2) / 2;
  }
  return ((x - x1) * (y2 - y1)) / (x2 - x1) + y1;
};

const clamp = (value: any) => (x: any) =>
  Math.max(
    0,
    Math.min(
      1,
      interpolate(
        x,
        0,
        0,
        (typeof window === 'undefined' ? 0 : window?.innerHeight) - 80 / 2,
        1,
      ),
    ),
  ) * value;

export const AnnualHero = ({ title, image, shadeOpacity }: IProps) => {
  const ref = useRef(null);
  const imgRef = useRef(null);
  const contentRef = useRef(null);

  const [{ y }, spring0] = useSpring(() => ({
    y: 0,
    // config: config.gentle,
  }));

  // animate scroll
  const [_, spring] = useSpring(() => ({
    y: 0,
    config: config.slow,
    onChange: (props) => {
      window.scrollTo(0, props.value.y);
    },
  }));

  // on scrolll animate y
  const onScroll = () => {
    const y = window.scrollY;
    spring0.start({ y });
  };

  // add scroll events
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onWheelScroll = () => spring.stop();
    window.addEventListener('scroll', onScroll);
    window.addEventListener('wheel', onWheelScroll, false);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('wheel', onWheelScroll, false);
    };
  }, []);
  return (
    <Host ref={ref} id="hero">
      <Image
        ref={imgRef}
        style={{
          y: y.to(clamp(-150)),
        }}
      >
        {image}
      </Image>

      <Content ref={contentRef} shadeOpacity={shadeOpacity}>
        <Text
          style={{
            y: y.to(clamp(-450)),
          }}
        >
          <H1>{title}</H1>
        </Text>
        <Button
          onClick={() => {
            spring.start({
              y: window.innerHeight,
              reset: true,
              from: { y: window.scrollY },
            });
          }}
          style={{
            y: y.to(clamp(-350)),
          }}
        >
          <ScrollDownArrow />
        </Button>
      </Content>
      <SmoothWhiteArea style={{ y: y.to(clamp(-50)) }} />
    </Host>
  );
};
